import React, { useState } from 'react';
import {
  FiActivity,
  FiArrowLeft,
  FiArrowRight,
  FiAward,
  FiDownload,
  FiGrid,
  FiInfo,
  FiList,
  FiMeh,
  FiMessageSquare,
  FiMinus,
  FiSearch,
  FiSettings,
  FiSmile,
  FiTarget,
  FiThumbsDown,
  FiTrendingDown,
  FiTrendingUp,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import VerbatimCard from './VerbatimCard';
import { Timestamp } from './ui/timestamp';
import { feedbackItems, productInsights } from './ConsumerChampion.data';

// Consumer Champion Dashboard - Product Feedback & Insights
const ConsumerChampion: React.FC = () => {
  // State management
  const [activeView, setActiveView] = useState<'overview' | 'insight' | 'feedback'>('overview');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | 'all'>('all');
  const [filterSentiment, setFilterSentiment] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');
  const [dismissedActions, setDismissedActions] = useState<string[]>([]);

  // Helper functions
  const getInsightById = (id: string) => {
    return productInsights.find(insight => insight.id === id);
  };
  
  const getFeedbackById = (id: string) => {
    return feedbackItems.find(item => item.id === id);
  };
  
  const getFeedbackByInsightId = (insightId: string) => {
    return feedbackItems.filter(item => item.insightId === insightId);
  };

  // Filter insights based on search term, category, and sentiment
  const filteredInsights = productInsights.filter(insight => {
    const matchesSearch = searchTerm === '' || 
      insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.topic.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || insight.category === selectedCategory;
    
    const matchesSentiment = filterSentiment === 'all' || insight.sentiment === filterSentiment;
    
    return matchesSearch && matchesCategory && matchesSentiment;
  });

  // Get unique categories for filter
  const categories = Array.from(new Set(productInsights.map(insight => insight.category)));

  // Handle view changes
  const handleViewInsight = (insightId: string) => {
    setSelectedInsight(insightId);
    setActiveView('insight');
  };
  
  const handleViewFeedback = (feedbackId: string) => {
    setSelectedFeedback(feedbackId);
    setActiveView('feedback');
  };
  
  const handleBackToOverview = () => {
    setActiveView('overview');
    setSelectedInsight(null);
    setSelectedFeedback(null);
  };

  const handleBackToInsight = () => {
    setActiveView('insight');
    setSelectedFeedback(null);
  };

  // Helper function for risk/scoring colors
  const getSentimentClass = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return 'bg-green-100 text-green-700';
      case 'negative':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  };

  const getSentimentColorClass = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-rose-600';
      default:
        return 'text-amber-600';
    }
  };

  const getScoreClass = (score: number) => {
    if (score >= 85) return 'bg-green-100 text-green-700';
    if (score >= 70) return 'bg-amber-100 text-amber-700';
    return 'bg-rose-100 text-rose-700';
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return FiSmile;
      case 'negative':
        return FiThumbsDown;
      default:
        return FiMeh;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'growing':
        return FiTrendingUp;
      case 'decreasing':
        return FiTrendingDown;
      default:
        return FiMinus;
    }
  };

  const getTrendClass = (trend: string) => {
    switch (trend) {
      case 'growing':
        return 'text-green-600';
      case 'decreasing':
        return 'text-rose-600';
      default:
        return 'text-amber-600';
    }
  };

  // Render functions for each view
  const renderOverview = () => {
    return (
      <div>
        {/* Dashboard Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <IconWrapper icon={FiAward} className="mr-3 text-amber-500" size={28} />
              Consumer Champion
            </h1>
            <p className="text-secondary/70">
              Identify product feedback trends and opportunities for improvement
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg flex items-center hover:bg-gray-50 text-sm">
              <IconWrapper icon={FiDownload} className="mr-2" size={16} />
              Export Insights
            </button>
            <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg flex items-center hover:bg-gray-50 text-sm">
              <IconWrapper icon={FiSettings} className="mr-2" size={16} />
              Preferences
            </button>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="col-span-1 md:col-span-2">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <IconWrapper icon={FiSearch} size={18} />
              </span>
              <input
                type="text"
                placeholder="Search insights by keyword, category, or topic..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <select
              className="w-full py-2 px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              className="w-full py-2 px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={filterSentiment}
              onChange={(e) => setFilterSentiment(e.target.value as any)}
            >
              <option value="all">All Sentiment</option>
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
            </select>
          </div>
        </div>
        
        {/* View Mode Toggle */}
        <div className="mb-4 flex justify-between items-center">
          <div className="text-sm text-secondary/70">
            Showing {filteredInsights.length} product insights
          </div>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              className={`px-3 py-1.5 flex items-center ${viewMode === 'cards' ? 'bg-primary text-white' : 'bg-white text-secondary hover:bg-gray-50'}`}
              onClick={() => setViewMode('cards')}
            >
              <IconWrapper icon={FiGrid} size={16} className="mr-1" />
              Cards
            </button>
            <button
              className={`px-3 py-1.5 flex items-center ${viewMode === 'table' ? 'bg-primary text-white' : 'bg-white text-secondary hover:bg-gray-50'}`}
              onClick={() => setViewMode('table')}
            >
              <IconWrapper icon={FiList} size={16} className="mr-1" />
              Table
            </button>
          </div>
        </div>
        
        {/* Insights List */}
        {viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInsights.map((insight) => (
              <div 
                key={insight.id}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleViewInsight(insight.id)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-2 rounded-lg ${getSentimentClass(insight.sentiment)}`}>
                    <IconWrapper icon={getSentimentIcon(insight.sentiment)} size={20} />
                  </div>
                  <div className="flex items-center">
                    <div className={`px-2 py-1 text-xs font-semibold rounded-full mr-2 ${getScoreClass(insight.score)}`}>
                      {insight.score}
                    </div>
                    <div className={`text-sm ${getTrendClass(insight.impactStatus)}`}>
                      <IconWrapper icon={getTrendIcon(insight.impactStatus)} size={16} />
                    </div>
                  </div>
                </div>
                
                <h3 className="font-medium text-secondary mb-1 line-clamp-2">{insight.title}</h3>
                <p className="text-sm text-secondary/70 mb-3 line-clamp-2">{insight.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                    {insight.category}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                    {insight.topic}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-xs text-secondary/70">
                  <div className="flex items-center">
                    <IconWrapper icon={FiUsers} size={14} className="mr-1" />
                    {insight.count} feedbacks
                  </div>
                  <div>
                    <Timestamp value={insight.lastDetected} format="date-only" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Insight
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Category & Topic
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Sentiment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Score
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Feedback Count
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Trend
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary/70 uppercase tracking-wider">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInsights.map((insight) => (
                  <tr 
                    key={insight.id}
                    onClick={() => handleViewInsight(insight.id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-secondary">{insight.title}</div>
                      <div className="text-xs text-secondary/70 mt-1">{insight.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full inline-block w-fit">
                          {insight.category}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full inline-block w-fit">
                          {insight.topic}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <IconWrapper 
                          icon={getSentimentIcon(insight.sentiment)} 
                          size={16} 
                          className={`mr-1 ${getSentimentColorClass(insight.sentiment)}`} 
                        />
                        <span className="capitalize">{insight.sentiment}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`px-2 py-1 text-xs font-semibold rounded-full w-fit ${getScoreClass(insight.score)}`}>
                        {insight.score}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {insight.count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center ${getTrendClass(insight.impactStatus)}`}>
                        <IconWrapper icon={getTrendIcon(insight.impactStatus)} size={16} className="mr-1" />
                        <span className="capitalize">{insight.impactStatus}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary/70">
                      <Timestamp value={insight.lastDetected} format="date-only" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderInsightDetail = () => {
    if (!selectedInsight) return null;
    
    const insight = getInsightById(selectedInsight);
    if (!insight) return null;
    
    const relatedFeedback = getFeedbackByInsightId(insight.id);
    
    return (
      <div>
        {/* Navigation */}
        <div className="mb-6">
          <button 
            className="flex items-center text-primary hover:underline"
            onClick={handleBackToOverview}
          >
            <IconWrapper icon={FiArrowLeft} className="mr-1" size={16} />
            Back to all insights
          </button>
        </div>
        
        {/* Insight Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center mb-3">
                <div className={`p-2 rounded-lg mr-3 ${getSentimentClass(insight.sentiment)}`}>
                  <IconWrapper icon={getSentimentIcon(insight.sentiment)} size={24} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-secondary">{insight.title}</h1>
                  <p className="text-secondary/70 mt-1">{insight.description}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                  {insight.category}
                </span>
                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                  {insight.topic}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full flex items-center ${getSentimentClass(insight.sentiment)}`}>
                  <IconWrapper icon={getSentimentIcon(insight.sentiment)} size={12} className="mr-1" />
                  {insight.sentiment.charAt(0).toUpperCase() + insight.sentiment.slice(1)}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <div className={`text-3xl font-bold ${getSentimentColorClass(insight.sentiment)}`}>
                {insight.score}
              </div>
              <div className="text-sm text-secondary/70">Insight Score</div>
              <div className={`flex items-center mt-2 ${getTrendClass(insight.impactStatus)}`}>
                <IconWrapper icon={getTrendIcon(insight.impactStatus)} size={16} className="mr-1" />
                <span className="capitalize">{insight.impactStatus}</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-secondary mb-2 flex items-center">
                <IconWrapper icon={FiTarget} className="mr-2 text-blue-600" size={16} />
                Key Points
              </h3>
              <ul className="space-y-2">
                {insight.keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <span className="mr-2 text-primary">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-secondary mb-2 flex items-center">
                <IconWrapper icon={FiActivity} className="mr-2 text-green-600" size={16} />
                Suggested Actions
              </h3>
              <ul className="space-y-2">
                {insight.suggestedActions.map((action, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <span className="mr-2 text-primary">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-secondary mb-2 flex items-center">
                <IconWrapper icon={FiInfo} className="mr-2 text-purple-600" size={16} />
                Insight Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <div className="text-secondary/70">Feedback Count:</div>
                  <div className="font-medium">{insight.count}</div>
                </div>
                <div className="flex justify-between">
                  <div className="text-secondary/70">First Detected:</div>
                  <div><Timestamp value={insight.firstDetected} format="date-only" /></div>
                </div>
                <div className="flex justify-between">
                  <div className="text-secondary/70">Last Updated:</div>
                  <div><Timestamp value={insight.lastDetected} format="date-only" /></div>
                </div>
                <div className="flex justify-between">
                  <div className="text-secondary/70">Sources:</div>
                  <div className="text-right">{insight.feedbackSources.join(', ')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Related Feedback */}
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <IconWrapper icon={FiMessageSquare} className="mr-2 text-blue-600" size={20} />
            Related Customer Feedback 
            <span className="ml-2 px-2 py-0.5 text-sm bg-gray-100 text-secondary/70 rounded-full">
              {relatedFeedback.length}
            </span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedFeedback.map(feedback => (
              <div 
                key={feedback.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleViewFeedback(feedback.id)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className={`p-1 rounded-full mr-2 ${getSentimentClass(feedback.sentiment)}`}>
                      <IconWrapper icon={getSentimentIcon(feedback.sentiment)} size={14} />
                    </div>
                    <div className="text-sm font-medium">{feedback.customer}</div>
                  </div>
                  <div className="text-xs text-secondary/70">
                    <Timestamp value={feedback.date} format="date-only" />
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="text-sm text-secondary line-clamp-3">{feedback.verbatim}</p>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="text-xs text-secondary/70">Source: {feedback.source}</div>
                  <button className="text-primary text-sm hover:underline flex items-center">
                    View Details
                    <IconWrapper icon={FiArrowRight} size={14} className="ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackDetail = () => {
    if (!selectedFeedback) return null;
    
    const feedback = getFeedbackById(selectedFeedback);
    if (!feedback) return null;
    
    const insight = getInsightById(feedback.insightId);
    
    return (
      <div>
        {/* Navigation */}
        <div className="mb-6">
          <button 
            className="flex items-center text-primary hover:underline"
            onClick={() => insight ? handleViewInsight(insight.id) : handleBackToOverview()}
          >
            <IconWrapper icon={FiArrowLeft} className="mr-1" size={16} />
            {insight ? `Back to insight: ${insight.title}` : 'Back to all insights'}
          </button>
        </div>
        
        {/* Feedback Detail */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="flex items-center mb-2">
                <div className={`p-1.5 rounded-full mr-2 ${getSentimentClass(feedback.sentiment)}`}>
                  <IconWrapper icon={getSentimentIcon(feedback.sentiment)} size={16} />
                </div>
                <h2 className="text-xl font-bold text-secondary">Customer Feedback</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                  {feedback.category}
                </span>
                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                  {feedback.topic}
                </span>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-secondary/70 rounded-full">
                  {feedback.source}
                </span>
              </div>
            </div>
            <div className="text-sm text-secondary/70">
              <Timestamp value={feedback.date} format="absolute" />
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-2">
              <IconWrapper icon={FiUser} className="mr-2 text-blue-600" size={16} />
              <div className="font-medium">{feedback.customer}</div>
            </div>
            <p className="text-secondary mb-4">"{feedback.verbatim}"</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <VerbatimCard 
                text={feedback.verbatim}
                sentiment={feedback.sentiment}
                sentimentScore={feedback.sentimentScore}
                emotions={feedback.topEmotions}
                keywords={feedback.keywords}
              />
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-secondary mb-3 flex items-center">
                <IconWrapper icon={FiInfo} className="mr-2 text-purple-600" size={16} />
                Related Product Insight
              </h3>
              
              {insight && (
                <div 
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewInsight(insight.id)}
                >
                  <div className="flex items-center mb-2">
                    <div className={`p-1.5 rounded-lg mr-2 ${getSentimentClass(insight.sentiment)}`}>
                      <IconWrapper icon={getSentimentIcon(insight.sentiment)} size={16} />
                    </div>
                    <h4 className="font-medium">{insight.title}</h4>
                  </div>
                  <p className="text-sm text-secondary/70 mb-2">{insight.description}</p>
                  <div className="flex justify-between">
                    <div className={`px-2 py-0.5 text-xs font-semibold rounded ${getScoreClass(insight.score)}`}>
                      Score: {insight.score}
                    </div>
                    <div className="text-sm text-primary flex items-center">
                      View Detail
                      <IconWrapper icon={FiArrowRight} size={14} className="ml-1" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render logic based on active view
  return (
    <div className="p-6">
      {activeView === 'overview' && renderOverview()}
      {activeView === 'insight' && renderInsightDetail()}
      {activeView === 'feedback' && renderFeedbackDetail()}
    </div>
  );
};

export default ConsumerChampion;